import { ImageUp, Pencil, Plus, Trash2 } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { toast } from 'sonner';

import { api, unwrap } from '@/api/client';
import { mensajeDe } from '@/api/errors';
import { useCan } from '@/auth/queries';
import { Campo } from '@/components/Campo';
import { Vacio } from '@/components/Estados';
import { Modal } from '@/components/Modal';
import { OrdenableLista } from '@/components/OrdenableLista';
import { SubirVarios, type ArchivoSubidoCola } from '@/components/SubirVarios';
import { ACEPTA, subirArchivo, validarArchivo, type TipoArchivo } from '@/lib/archivos';
import { reconocerVideo, urlDeVideo } from '@/lib/video';

import {
  rutaClase,
  rutaGrupo,
  rutaHijo,
  useClaseMutacion,
  type Clase,
  type GrupoRecursos,
} from './queries';

type VideoClase = Clase['videos'][number];

function ModalVideo({
  c,
  video,
  onCerrar,
}: {
  c: Clase;
  video: VideoClase | null;
  onCerrar: () => void;
}) {
  const [f, setF] = useState({
    name: video?.name ?? '',
    videoUrl: urlDeVideo(video?.video ?? null),
    appUrl: video?.appUrl ?? '',
  });
  const reconocido = reconocerVideo(f.videoUrl);
  const guardar = useClaseMutacion(
    c.id,
    () => {
      const body = {
        name: f.name.trim(),
        videoUrl: f.videoUrl.trim(),
        appUrl: f.appUrl.trim() || null,
      };
      return unwrap<Clase>(
        video
          ? api.PATCH('/api/v1/admin/classes/{id}/videos/{childId}', {
              ...rutaHijo(c.id, video.id),
              body,
            })
          : api.POST('/api/v1/admin/classes/{id}/videos', { ...rutaClase(c.id), body }),
      );
    },
    video ? 'Video guardado.' : 'Video agregado.',
  );
  return (
    <Modal
      abierto
      onCerrar={onCerrar}
      titulo={video ? 'Editar video' : 'Nuevo video'}
      ancho="max-w-2xl"
    >
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          if (!reconocido) toast.error('Escribe una URL de YouTube o Vimeo válida.');
          else guardar.mutate(undefined, { onSuccess: onCerrar });
        }}
      >
        <Campo etiqueta="Nombre">
          <input
            className="input-vidrio"
            required
            minLength={2}
            maxLength={100}
            value={f.name}
            onChange={(e) => setF({ ...f, name: e.target.value })}
          />
        </Campo>
        <Campo
          etiqueta="URL del video"
          ayuda="YouTube o Vimeo."
          error={f.videoUrl && !reconocido ? 'No parece una URL de YouTube o Vimeo.' : undefined}
        >
          <input
            className="input-vidrio"
            required
            value={f.videoUrl}
            onChange={(e) => setF({ ...f, videoUrl: e.target.value })}
          />
        </Campo>
        {reconocido && (
          <div className="aspect-video w-full overflow-hidden rounded-xl bg-black">
            <iframe
              src={reconocido.embedUrl}
              title="Vista previa"
              className="h-full w-full"
              allow="fullscreen"
            />
          </div>
        )}
        <Campo etiqueta="URL de la app">
          <input
            className="input-vidrio"
            maxLength={250}
            value={f.appUrl}
            onChange={(e) => setF({ ...f, appUrl: e.target.value })}
          />
        </Campo>
        <div className="flex justify-end gap-2">
          <button type="button" className="btn-cs btn-contorno" onClick={onCerrar}>
            Cancelar
          </button>
          <button type="submit" className="btn-cs btn-primario" disabled={guardar.isPending}>
            Guardar
          </button>
        </div>
      </form>
    </Modal>
  );
}

/** Master Class: videos de la clase en orden. */
export function PestanaVideos({ c }: { c: Clase }) {
  const editable = useCan('contenido', 'editar');
  const [modal, setModal] = useState<{ video: VideoClase | null } | null>(null);
  const ordenar = useClaseMutacion(c.id, (ids: number[]) =>
    unwrap<Clase>(
      api.PUT('/api/v1/admin/classes/{id}/videos/order', { ...rutaClase(c.id), body: { ids } }),
    ),
  );
  const quitar = useClaseMutacion(
    c.id,
    (childId: number) =>
      unwrap<Clase>(
        api.DELETE('/api/v1/admin/classes/{id}/videos/{childId}', rutaHijo(c.id, childId)),
      ),
    'Video quitado.',
  );
  return (
    <div className="max-w-3xl space-y-3">
      {editable && (
        <button
          type="button"
          className="btn-cs btn-primario"
          onClick={() => setModal({ video: null })}
        >
          <Plus size={16} aria-hidden /> Agregar video
        </button>
      )}
      {c.videos.length === 0 && <Vacio titulo="Sin videos" />}
      <OrdenableLista
        items={c.videos}
        idDe={(v) => v.id}
        deshabilitado={!editable || ordenar.isPending}
        className="space-y-2"
        onOrdenar={(ids) => ordenar.mutate(ids)}
      >
        {(v, asa, i) => (
          <div className="panel-vidrio flex items-center gap-3 p-3">
            {asa}
            <span className="tabular text-texto-3">{i + 1}</span>
            <div className="min-w-0 flex-1">
              <p className="truncate font-semibold">{v.name}</p>
              <p className="text-xs text-texto-3">
                {v.video?.provider === 'vimeo'
                  ? 'Vimeo'
                  : v.video?.provider === 'youtube'
                    ? 'YouTube'
                    : '—'}{' '}
                {v.video?.videoId}
              </p>
            </div>
            {editable && (
              <>
                <button
                  type="button"
                  className="btn-cs btn-vidrio px-2"
                  aria-label={`Editar ${v.name}`}
                  onClick={() => setModal({ video: v })}
                >
                  <Pencil size={14} aria-hidden />
                </button>
                <button
                  type="button"
                  className="btn-cs btn-vidrio px-2 text-rojo-texto"
                  aria-label={`Quitar ${v.name}`}
                  onClick={() => quitar.mutate(v.id)}
                >
                  <Trash2 size={14} aria-hidden />
                </button>
              </>
            )}
          </div>
        )}
      </OrdenableLista>
      {modal && <ModalVideo c={c} video={modal.video} onCerrar={() => setModal(null)} />}
    </div>
  );
}

/** Destino de subida y formatos de cada grupo de recursos (carpetas que lee la plataforma). */
function destinoDe(grupo: GrupoRecursos, taller: boolean) {
  if (grupo === 'english')
    return { destino: 'flashcard' as const, tipos: ['image'] as TipoArchivo[] };
  if (grupo === 'plus')
    return taller
      ? { destino: 'flashcard' as const, tipos: ['image'] as TipoArchivo[] }
      : { destino: 'clase' as const, tipos: ['image'] as TipoArchivo[] };
  return taller
    ? { destino: 'guia-gpc' as const, tipos: ['image', 'pdf'] as TipoArchivo[] }
    : { destino: 'clase' as const, tipos: ['image', 'pdf'] as TipoArchivo[] };
}

type Recurso = Clase['resources']['english'][number];

function TarjetaRecurso({
  c,
  r,
  grupo,
  asa,
}: {
  c: Clase;
  r: Recurso;
  grupo: GrupoRecursos;
  asa: ReactNode;
}) {
  const editable = useCan('contenido', 'editar');
  const [nombre, setNombre] = useState(r.name);
  const [subiendo, setSubiendo] = useState(false);
  const { destino, tipos } = destinoDe(grupo, c.isWorkshop);
  const editar = useClaseMutacion(c.id, (body: { name?: string; filePath?: string }) =>
    unwrap<Clase>(
      api.PATCH('/api/v1/admin/classes/{id}/resources/{childId}', {
        ...rutaHijo(c.id, r.id),
        body,
      }),
    ),
  );
  const quitar = useClaseMutacion(
    c.id,
    () =>
      unwrap<Clase>(
        api.DELETE('/api/v1/admin/classes/{id}/resources/{childId}', rutaHijo(c.id, r.id)),
      ),
    'Quitado.',
  );
  const pdf = /\.pdf$/i.test(r.filePath ?? '');
  return (
    <div className="panel-vidrio space-y-2 p-2">
      <div className="flex items-center gap-1 text-xs text-texto-3">{asa}</div>
      <a href={r.fileUrl ?? undefined} target="_blank" rel="noreferrer" className="block">
        {r.fileUrl && !pdf ? (
          <img
            src={r.fileUrl}
            alt={r.name}
            loading="lazy"
            className="aspect-[4/3] w-full rounded bg-black/5 object-contain"
          />
        ) : (
          <span className="grid aspect-[4/3] w-full place-items-center rounded bg-black/5 text-xs text-texto-3">
            PDF
          </span>
        )}
      </a>
      <input
        className="input-vidrio py-1 text-xs"
        aria-label="Nombre"
        value={nombre}
        maxLength={250}
        disabled={!editable}
        onChange={(e) => setNombre(e.target.value)}
        onBlur={() => nombre.trim() && nombre !== r.name && editar.mutate({ name: nombre.trim() })}
      />
      {editable && (
        <div className="flex gap-1">
          <label className="btn-cs btn-vidrio flex-1 cursor-pointer px-2 py-1 text-xs">
            <ImageUp size={14} aria-hidden /> {subiendo ? 'Subiendo…' : 'Reemplazar'}
            <input
              type="file"
              className="sr-only"
              accept={tipos.map((t) => ACEPTA[t]).join(',')}
              onChange={(e) => {
                const file = e.target.files?.[0];
                e.target.value = '';
                if (!file) return;
                const error = validarArchivo(file, tipos);
                if (error) return void toast.error(error);
                setSubiendo(true);
                void subirArchivo(file, destino, destino === 'clase' ? c.id : undefined)
                  .then((s) => editar.mutateAsync({ filePath: s.path }))
                  .catch((err: unknown) => toast.error(mensajeDe(err)))
                  .finally(() => setSubiendo(false));
              }}
            />
          </label>
          <button
            type="button"
            className="btn-cs btn-vidrio px-2 py-1 text-rojo-texto"
            aria-label={`Quitar ${r.name}`}
            onClick={() => quitar.mutate(undefined)}
          >
            <Trash2 size={14} aria-hidden />
          </button>
        </div>
      )}
    </div>
  );
}

/** Inglés Médico, ENARM Plus o Guías GPC de la clase. */
export function PestanaRecursos({ c, grupo }: { c: Clase; grupo: GrupoRecursos }) {
  const editable = useCan('contenido', 'editar');
  const items = c.resources[grupo];
  const [subiendo, setSubiendo] = useState(false);
  const [listos, setListos] = useState<ArchivoSubidoCola[]>([]);
  const [pendientes, setPendientes] = useState(0);
  const { destino, tipos } = destinoDe(grupo, c.isWorkshop);
  const agregar = useClaseMutacion(
    c.id,
    () =>
      unwrap<Clase>(
        api.POST('/api/v1/admin/classes/{id}/resources/{group}', {
          ...rutaGrupo(c.id, grupo),
          body: { files: listos.map((a) => ({ name: a.nombre, filePath: a.path })) },
        }),
      ),
    'Archivos agregados.',
  );
  const ordenar = useClaseMutacion(c.id, (ids: number[]) =>
    unwrap<Clase>(
      api.PUT('/api/v1/admin/classes/{id}/resources/{group}/order', {
        ...rutaGrupo(c.id, grupo),
        body: { ids },
      }),
    ),
  );
  return (
    <div className="space-y-4">
      {editable && (
        <button type="button" className="btn-cs btn-primario" onClick={() => setSubiendo(true)}>
          <Plus size={16} aria-hidden /> Agregar archivos
        </button>
      )}
      {items.length === 0 && <Vacio titulo="Sin archivos" />}
      <OrdenableLista
        items={items}
        idDe={(r) => r.id}
        cuadricula
        deshabilitado={!editable || ordenar.isPending}
        className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5"
        onOrdenar={(ids) => ordenar.mutate(ids)}
      >
        {(r, asa) => (
          <TarjetaRecurso key={`${r.id}-${r.name}`} c={c} r={r} grupo={grupo} asa={asa} />
        )}
      </OrdenableLista>
      {subiendo && (
        <Modal
          abierto
          onCerrar={() => setSubiendo(false)}
          titulo="Agregar archivos"
          ancho="max-w-3xl"
        >
          <div className="space-y-4">
            <SubirVarios
              destino={destino}
              ownerId={destino === 'clase' ? c.id : undefined}
              tipos={tipos}
              maxNombre={250}
              onCambio={(l, n) => {
                setListos(l);
                setPendientes(n);
              }}
            />
            <div className="flex justify-end gap-2">
              <button
                type="button"
                className="btn-cs btn-contorno"
                onClick={() => setSubiendo(false)}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="btn-cs btn-primario"
                disabled={listos.length === 0 || pendientes > 0 || agregar.isPending}
                onClick={() => agregar.mutate(undefined, { onSuccess: () => setSubiendo(false) })}
              >
                Agregar {listos.length}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
